import { useEffect, useMemo, useRef, useState } from "react";
import { fmtAed } from "../components/KpiCard";
import type { ReconSettings } from "../types/api";
import TrackColumn from "../components/TrackColumn";
import AdditionalDetails from "../components/AdditionalDetails";
import InfoTip from "../components/InfoTip";
import { api, ApiError, setViewSegment, viewSegment } from "../api/client";
import { useToast } from "../components/Toast";
import type { ReconResult, TrackSummaryRow, GroupBreakdownRow, ManualMatchPair, FrameRecords } from "../types/api";
import { DEFAULT_USD_RATE, DEFAULT_EUR_RATE, type SidebarSettings } from "../types/ui";
import type { ReconInputs } from "./UploadPage";
import Sidebar from "../components/Sidebar";
import { monthEndISO } from "../components/ReconMonthDialog";
import * as sessionRestore from "../lib/sessionRestore";
import { buildAlerts, type Alert } from "../lib/alerts";
import { setRawMoneyColumns } from "../lib/columns";

interface Props {
  result: ReconResult;
  prematchId: string;
  inputs: ReconInputs;
  sidebar: SidebarSettings;
  onSidebarChange: (s: SidebarSettings) => void;
  // Rates in force from the user's saved set; passed to the sidebar for its notice,
  // along with the session-scoped acknowledgement of that notice.
  savedRates?: { usd?: number; eur?: number };
  rateNoticeAck?: string;
  onAckRateNotice?: (sig: string) => void;
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  onReupload: () => void;
}

// In-window rows the engine could place in NO bucket (recon_category "Other") — the
// population the Difference actually consists of. Mirrors the two sections OtherTab
// badges "IN THE DIFFERENCE" (Unidentified + Identified but not reconciled), less the
// 0.00 settled held-out rows OtherTab lists under "Settled - nothing outstanding".
function countUnplaced(f?: FrameRecords): number {
  if (!f || !f.rows.length) return 0;
  const cCat = f.columns.indexOf("recon_category");
  if (cCat < 0) return 0;
  const cWin = f.columns.indexOf("in_period_window");
  const cRole = f.columns.indexOf("role");
  const cSettled = f.columns.indexOf("is_settled_paid");
  let n = 0;
  for (const r of f.rows) {
    // Absent column (no window applied) => in-period, matching the engine's fillna(True).
    if (cWin >= 0 && r[cWin] === false) continue;
    if (String(r[cCat] ?? "") !== "Other") continue;
    if (cSettled >= 0 && r[cSettled] === true && cRole >= 0 && String(r[cRole] ?? "") !== "Other") continue;
    n++;
  }
  return n;
}

// DEFERRED FRAMES.
//
// The two transaction frames are ~98.6% of a /reconcile response (Dubai: 74.5 MB of
// 75.6 MB) and the dashboard paints without reading a single row of them — headline
// balances, both track panels, the group breakdown, the verdict and every alert are
// scalars the engine already computed. The rows are consumed only behind the statement
// view and the drill-down expanders. So the run asks for figures first and fetches the
// rows separately (api.frames), which is what takes Dubai's click-to-dashboard from
// ~9s to ~2s.
//
// The INVARIANT every downstream component relies on — a ReconResult in hand always has
// both frames — is preserved here rather than pushed outward: an empty stand-in fills
// them until the real rows land, and `framesReady` gates every control that would read a
// row. Nothing can render a half-loaded frame, because nothing frame-dependent is
// reachable before framesReady. (ManualNotesTab already worked this way: it hands back
// the CURRENT result rather than its own frameless response.)
const EMPTY_FRAME: FrameRecords = { columns: [], rows: [], indices: [], total_rows: 0 };

// The message alone: String(e) prefixes "Error: ", which reads as a crash to finance.
function errText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

// A re-run whose response landed after a NEWER re-run had started. Its figures were
// computed from superseded settings, so it is dropped rather than shown; the newer run
// reports its own outcome, so callers that toast errors stay quiet on this one.
class SupersededRun extends Error {
  constructor() {
    super("A newer change is being applied; the figures will update when it finishes.");
  }
}

function hasFrames(r: ReconResult): boolean {
  return !!(r.df_cri && r.df_insurer);
}

function withFrames(r: ReconResult): ReconResult {
  return hasFrames(r) ? r : { ...r, df_cri: EMPTY_FRAME, df_insurer: EMPTY_FRAME };
}

// Screen 2 — dashboard. Mirrors app/dashboard.py::_render_dashboard: header + badges,
// the TWO full-ledger balance cards, combined opening, live-tolerance caption, the
// two-column track view (four groups each), the centered Difference banner, then the
// statement tabs. All figures come from the engine (via the API), never recomputed here.
export default function ResultPage({ result: initialResult, prematchId: initialPrematchId, inputs, sidebar, onSidebarChange, savedRates, rateNoticeAck, onAckRateNotice, sidebarCollapsed, onToggleSidebar, onReupload }: Props) {
  const { insurerName } = inputs;
  const toast = useToast();
  const [exporting, setExporting] = useState(false);
  // Live result + session manual-match pairs: applying a manual match re-finalizes
  // and swaps in the new result (the instant-edit path), so every figure updates.
  const [result, setResult] = useState<ReconResult>(() => withFrames(initialResult));
  // The frames as the last FULL run produced them (initial load or a re-run). A manual
  // match returns only its Tier-6 rows, which ManualMatchTab patches onto THIS — never
  // onto the already-patched `result`, so removing a match correctly reverts its rows.
  // Only a full-frame response may replace it.
  const [baseline, setBaseline] = useState<ReconResult>(() => withFrames(initialResult));
  // During render, not in an effect: the grids and search indexes below format cells in
  // this same pass.
  setRawMoneyColumns(result.display_config);
  const [prematchId, setPrematchId] = useState<string>(initialPrematchId);
  // Are the real transaction rows in hand? False means `result`/`baseline` carry the
  // empty stand-in and no frame-reading control may be offered yet.
  const [framesReady, setFramesReady] = useState(() => hasFrames(initialResult));
  // Whether the statement view is open, which is what makes the rows NEEDED. A re-run
  // while it is open asks for them inline (one round trip, exactly as before this
  // change); while it is closed the run stays lean and the rows are fetched after.
  const [detailsOpen, setDetailsOpen] = useState(false);
  // Seeded from a restored session so a refresh keeps the edits the restored result
  // already carries (and the save effect below never overwrites them with empties).
  const [sessionPairs, setSessionPairs] = useState<ManualMatchPair[]>(() => inputs.restored?.sessionPairs ?? []);
  const [notesCri, setNotesCri] = useState<Record<string, string>>(() => inputs.restored?.notesCri ?? {});
  const [notesInsurer, setNotesInsurer] = useState<Record<string, string>>(() => inputs.restored?.notesInsurer ?? {});
  // The settings the CURRENT result was computed with. Starts at the Run-time
  // settings and moves with every live sidebar recompute (below), so exports and
  // bridge re-runs always reflect what's on screen.
  const [reconSettings, setReconSettings] = useState<ReconSettings>(inputs.settings);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState("");
  // The alert stack sits at the top; re-runs are often triggered from deep in a tab,
  // so the toast is what the user actually sees. The alert stays as the record.
  useEffect(() => {
    if (applyError) toast.error(applyError);
  }, [applyError, toast]);
  // Rejected bridge (auto-match) signatures. Removing an auto-match changes the
  // PREMATCH (bridge runs there), so it re-calls /reconcile, not /finalize — and the
  // prematch_id changes, so we track it in state.
  const [bridgeRejections, setBridgeRejections] = useState<string[]>(() => inputs.restored?.bridgeRejections ?? []);
  const [rebridging, setRebridging] = useState(false);
  // Elapsed-seconds ticker for the long ops on this page (sidebar recompute, bridge
  // re-run, export) so none of them reads as hung.
  const [elapsed, setElapsed] = useState(0);
  const ticking = applying || rebridging || exporting;
  useEffect(() => {
    if (!ticking) { setElapsed(0); return; }
    const t0 = Date.now();
    const iv = setInterval(() => setElapsed(Math.round((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(iv);
  }, [ticking]);
  // Persist the identifying scalars (never the frames) so a refresh can replay them
  // through /finalize. Cheap: a few KB of JSON on changes the user makes by hand.
  useEffect(() => {
    sessionRestore.save({
      prematchId, insurerName,
      insurerSheetName: inputs.insurerSheetName,
      // The book this reconciliation was run in, so a refresh restores it under the
      // right book instead of whatever the tab happened to resolve to.
      book: inputs.book,
      customerKey: result.customer_key,
      segment: result.segment || undefined,
      sessionPairs, notesCri, notesInsurer, bridgeRejections,
      reconSettings, sidebar,
    });
  }, [prematchId, insurerName, inputs.insurerSheetName, inputs.book, sessionPairs,
      notesCri, notesInsurer, bridgeRejections, reconSettings, sidebar, result.segment]);

  // The slice on screen is the one every re-finalize asks for (api/client segmentBody),
  // so the module value follows the result the server actually sent back.
  useEffect(() => { setViewSegment(result.segment ?? ""); }, [result.segment]);

  // Which reconciliation is on screen right now. A frames fetch that resolves after a
  // re-run has moved on must not merge the OLD run's rows into the NEW result.
  const livePid = useRef(initialPrematchId);
  useEffect(() => { livePid.current = prematchId; }, [prematchId]);

  // Fetch the transaction rows for the reconciliation on screen. Deduplicated on the
  // prematch_id, so the idle prefetch below and an explicit "Show" click can never run
  // two at once. Silent on failure and it clears its own guard so a later attempt can
  // retry — the figures on screen are correct either way, and a failed row fetch must
  // never put an error banner over a sound reconciliation.
  const framesInFlight = useRef<string>("");
  async function loadFrames(pid: string): Promise<void> {
    // Keyed on the segment too: the rows of one slice must never land under another.
    const view = pid + "|" + viewSegment();
    if (!pid || framesInFlight.current === view) return;
    framesInFlight.current = view;
    try {
      const f = await api.frames({
        prematchId: pid, insurerName, sessionPairs,
        manualNotesCri: notesCri, manualNotesInsurer: notesInsurer,
      });
      // Superseded by a re-run or a segment switch.
      if (livePid.current + "|" + viewSegment() !== view) return;
      setResult((r) => ({ ...r, ...f }));
      setBaseline((b) => ({ ...b, ...f }));
      setFramesReady(true);
    } catch {
      framesInFlight.current = "";
    }
  }

  // Prefetch the rows once the dashboard has painted, so opening the statement view is
  // usually instant. Deliberately delayed rather than immediate: the first paint of a
  // large ledger is the exact moment this split exists to protect, and a 74 MB fetch
  // racing it would give the work straight back.
  useEffect(() => {
    if (framesReady) return;
    const t = setTimeout(() => { void loadFrames(prematchId); }, 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [framesReady, prematchId]);

  const s = result.summary;
  const reconciled = !!s.is_reconciled;
  const tolerance = sidebar.tolerance;
  const criTotal = result.cri_closing_total ?? result.cri_closing_combined ?? result.cri_closing;
  const insTotal = result.insurer_closing_total ?? result.insurer_closing_combined ?? result.insurer_closing;
  const opening = result.insurer_opening;
  const trackSummary: TrackSummaryRow[] = result.track_summary ?? [];
  const breakdown: GroupBreakdownRow[] = result.group_breakdown ?? [];
  const insurerTitle = result.insurer_name || "Insurer";
  // The header states the period RECONCILED: period_start is the data's own floor, so a
  // From window later than it (applied, or the CRI floor) is the real start.
  const windowFrom = result.period_from ? result.period_from.slice(0, 10) : null;
  const pillFrom = windowFrom && (!result.period_start || windowFrom > result.period_start)
    ? windowFrom : result.period_start;

  const premiumSummary = trackSummary.filter((r) => r.Track === "Premium");
  const commissionSummary = trackSummary.filter((r) => r.Track === "Commission");

  // AR has no commission track: commission is CRI revenue, never a customer receivable,
  // so the engine puts every AR row on Premium and the Commission panel is guaranteed
  // empty. Hiding it is DISPLAY ONLY - no figure and no engine path changes.
  const isAr = inputs.book === "AR";

  // Open items in Groups 1-3 — everything not in the Matched bucket. These are
  // RECONCILING items, not the Difference: every bucket cancels out of the summary
  // identity (backend/summary.py: difference = cri_closing - Σbuckets + insurer_closing
  // - matched_net), so matching a G1 item against a G2 one moves both from a bucket to
  // Matched and leaves the Difference to the cent. Used only for the verdict strip's
  // "what to do next" line; no figure is derived from it.
  const openItemCount = breakdown
    .filter((b) => b.Group !== "Matched")
    .reduce((n, b) => n + (typeof b.Count === "number" ? b.Count : 0), 0);

  // What the Difference IS: the bucketing residual — rows no bucket could hold. Same
  // line as above, no figure derived from it, so a truncated frame can only understate
  // the copy, never a number.
  const residualCount = useMemo(
    () => countUnplaced(result.df_cri) + countUnplaced(result.df_insurer),
    [result.df_cri, result.df_insurer],
  );

  // Full re-run against the SAME uploaded files with new settings / bridge set. The
  // server caches the prematch per settings-fingerprint, so revisiting a value is
  // instant. Session manual matches ride along (their content signatures survive a
  // re-run — same merge the Streamlit recompute did via _manual_matches_key); manual
  // notes are re-applied through /finalize when any exist.
  // A restored-after-refresh session has no File objects, so anything that re-runs the
  // PREMATCH (sidebar recompute, bridge rejection) is unavailable until re-upload.
  // The /finalize paths (manual match, notes, export) need only the prematch_id and
  // keep working normally.
  const filesLost = !inputs.criFiles?.length;

  // Bumped by every re-run. The sidebar stays live while one is in flight and a repeat of
  // earlier settings answers instantly from the server cache, so responses can land out
  // of order: without this, a slow older run overwrote the newer figures while the
  // sidebar kept showing the newer settings, and nothing re-ran to correct it.
  const rerunSeq = useRef(0);

  async function rerun(nextSettings: ReconSettings, nextRejections: string[]): Promise<string> {
    if (!inputs.criFiles?.length) {
      throw new Error(
        "The uploaded files aren't available after a page refresh. Use Re-upload to make this change."
      );
    }
    const seq = ++rerunSeq.current;
    const resp = await api.reconcile({
      insurerName,
      insurerSheetName: inputs.insurerSheetName,
      // The BOOK must ride along on every re-run. Omitted, the server reads the request
      // as AP and resolves the AP layout for this insurer — so on AR a sidebar
      // tolerance/rate/period change silently re-ran the WRONG book. It is part of the
      // run's identity (inputs.book), not a display flag.
      book: inputs.book,
      criFiles: inputs.criFiles,
      insurerFiles: inputs.insurerFiles,
      settings: nextSettings,
      bridgeRejections: nextRejections,
      insurerSheetFilter: inputs.insurerSheetFilter,
      sessionPairs,
      // WITHOUT THIS THE PRIOR-PERIOD BLOCK DISAPPEARS. The server rebuilds it from
      // the uploaded file on every /reconcile; omit the file and it returns
      // unreconciled=null, so the tab, its KPI cards and the Excel sheet all vanish —
      // on a tolerance change, a rate edit, a bridge rejection, or the re-run a
      // carried manual match itself triggers.
      // The sheet pins ride along too: dropping them would silently re-open the
      // most-items scan, which on finance's two-sided workbook can pick the OTHER
      // book's tab and restate the carried population against the wrong ledger.
      unreconciledFile: inputs.unreconciledFile,
      unreconciledSheetName: inputs.unreconciledSheetName,
      unreconciledCriFile: inputs.unreconciledCriFile,
      unreconciledCriSheetName: inputs.unreconciledCriSheetName,
      // The chosen AR customer, for the same reason as the book above.
      customerKey: inputs.customerKey,
      customerName: inputs.customerName,
      // Ask for the rows INLINE only when the statement view is already open — then this
      // stays the single round trip it has always been. With it closed the re-run carries
      // figures alone and the prefetch above collects the rows afterwards.
      includeFrames: detailsOpen,
      segment: viewSegment(),
    });
    if (seq !== rerunSeq.current) throw new SupersededRun();
    let final = resp.result;
    if (Object.keys(notesCri).length || Object.keys(notesInsurer).length) {
      const fin = await api.finalize({
        prematchId: resp.prematch_id, insurerName, sessionPairs,
        manualNotesCri: notesCri, manualNotesInsurer: notesInsurer,
        includeFrames: detailsOpen,
      });
      if (seq !== rerunSeq.current) throw new SupersededRun();
      final = fin.result;
    }
    setResult(withFrames(final));
    // A re-run ships full frames, so it re-establishes the patch baseline.
    setBaseline(withFrames(final));
    // The rows belong to the run that produced them: a re-run invalidates them, and the
    // prefetch re-arms on the new prematch_id.
    setFramesReady(hasFrames(final));
    framesInFlight.current = "";
    setPrematchId(resp.prematch_id);
    setReconSettings(nextSettings);
    setBridgeRejections(nextRejections);
    return resp.prematch_id;
  }

  // Show another display segment. The engine figures are the same run's, re-footed over
  // the slice server-side (backend/segments.py), so this is a /finalize, never a re-run:
  // it works after a refresh too. Shares the re-run sequence, so whichever was asked for
  // last is what stays on screen.
  async function switchSegment(next: string) {
    const prev = result.segment ?? "";
    if (next === prev) return;
    const seq = ++rerunSeq.current;
    setViewSegment(next);
    setApplying(true);
    setApplyError("");
    try {
      const fin = await api.finalize({
        prematchId, insurerName, sessionPairs,
        manualNotesCri: notesCri, manualNotesInsurer: notesInsurer,
        includeFrames: detailsOpen,
      });
      if (seq !== rerunSeq.current) return;
      setResult(withFrames(fin.result));
      // Another slice's rows: a new patch baseline, and the prefetch re-arms for them.
      setBaseline(withFrames(fin.result));
      setFramesReady(hasFrames(fin.result));
      framesInFlight.current = "";
    } catch (e) {
      if (seq !== rerunSeq.current) return;
      setViewSegment(prev);
      setApplyError("Could not switch the view: " + (e instanceof Error ? e.message : String(e)));
    } finally {
      if (seq === rerunSeq.current) setApplying(false);
    }
  }

  // Re-run reconcile with an updated set of rejected bridge signatures (the bridge is
  // in the prematch, so this is a full /reconcile with the same files + settings).
  // A carried-item pair was saved server-side. The Adjusted Closing is an ENGINE figure,
  // so the only honest way to reflect the pair is to re-run — never a client-side patch
  // of the totals. Same path the bridge rejections use.
  async function applyCarriedPairs() {
    setRebridging(true);
    setApplyError("");
    try {
      await rerun(reconSettings, bridgeRejections);
    } catch (e) {
      if (!(e instanceof SupersededRun)) setApplyError("Could not apply the match: " + errText(e));
    } finally {
      setRebridging(false);
    }
  }

  // The server's saved session (prematch) for this result expired or was evicted, and a
  // manual match or note needs it. Re-run against the files this tab still holds, the same
  // recovery the export uses, and hand back the new id for the caller to retry on.
  async function renewPrematch(): Promise<string> {
    if (filesLost) {
      throw new Error(
        "This reconciliation's saved session has expired, and after a page refresh the uploaded files are no longer in this tab. Use Re-upload to continue; your saved matches re-apply automatically."
      );
    }
    setRebridging(true);
    try {
      return await rerun(reconSettings, bridgeRejections);
    } finally {
      setRebridging(false);
    }
  }

  async function applyBridgeRejections(next: string[]) {
    setRebridging(true);
    setApplyError("");
    try {
      await rerun(reconSettings, next);
    } catch (e) {
      if (!(e instanceof SupersededRun)) setApplyError("Could not apply change: " + errText(e));
    } finally {
      setRebridging(false);
    }
  }

  // LIVE sidebar recompute — mirrors app/dashboard.py's tolerance / as-of / period
  // watcher: a change to the Matched tolerances or the date window re-runs the
  // reconciliation against the snapshotted upload (debounced; last-good result stays
  // on screen if the re-run fails). The badge tolerance alone doesn't re-run (same
  // as Streamlit) — it's read live by the track panels below.
  // A zero/blank rate is mid-typing, not an intent to reconcile at zero — hold the
  // last valid rate so the debounce never fires a run the server would 422.
  const effectiveUsdRate = sidebar.usdRate > 0 ? sidebar.usdRate : DEFAULT_USD_RATE;
  const effectiveEurRate = sidebar.eurRate > 0 ? sidebar.eurRate : DEFAULT_EUR_RATE;
  // periodToCleared is IN the key: clearing a To that the Run dialog only seeded for
  // display leaves periodTo at "" on both sides, so without this the key never moves
  // and Apply period re-runs nothing at all.
  const sidebarKey = [sidebar.tolPremium, sidebar.tolCommission, effectiveUsdRate, effectiveEurRate, sidebar.periodFrom ?? "", sidebar.periodTo ?? "", sidebar.periodToCleared ? "toclr" : ""].join("|");
  const lastKey = useRef(sidebarKey);
  useEffect(() => {
    if (sidebarKey === lastKey.current) return;
    // A restored session has no files to re-run against; keep the key in step so the
    // edit isn't replayed later, and leave the on-screen result untouched.
    if (filesLost) { lastKey.current = sidebarKey; return; }
    // Inverted window guard (mirrors the Streamlit warning): ignore, don't run empty.
    if (sidebar.periodFrom && sidebar.periodTo && sidebar.periodFrom > sidebar.periodTo) {
      setApplyError(`Period filter ignored: From (${sidebar.periodFrom}) is after To (${sidebar.periodTo}).`);
      return;
    }
    const t = setTimeout(async () => {
      lastKey.current = sidebarKey;
      setApplying(true);
      setApplyError("");
      try {
        await rerun({
          ...reconSettings,
          tolerance: sidebar.tolerance,
          amount_match_tol: sidebar.tolPremium,
          amount_tol_premium: sidebar.tolPremium,
          amount_tol_commission: sidebar.tolCommission,
          usd_rate: effectiveUsdRate,
          eur_rate: effectiveEurRate,
          period_from: sidebar.periodFrom,
          period_from_cleared: sidebar.periodFromCleared,
          period_to: sidebar.periodTo,
          // recon_month rides along in reconSettings and becomes the engine's
          // period_override, which bounds the top of the window exactly as To does. An
          // explicitly CLEARED To is the "no ceiling" gesture, so the month override has
          // to retire with it — otherwise the re-run re-imposed the chosen month-end and
          // the post-cutoff rows stayed excluded, making the clear look like a no-op.
          // Keyed on the explicit flag, not on a blank periodTo: To is blank on an
          // ordinary Automatic run too, and that must keep its (absent) override.
          // An APPLIED To retires it too: the box shows the month-end, so a later date
          // typed there is the user moving the ceiling, which tighter-wins ignored.
          recon_month: (sidebar.periodToCleared || sidebar.periodTo) ? "" : reconSettings.recon_month,
        }, bridgeRejections);
      } catch (e) {
        if (!(e instanceof SupersededRun)) setApplyError("Could not apply change: " + errText(e));
      } finally {
        setApplying(false);
      }
      // The sidebar now commits on blur/Enter rather than per keystroke, so this is no
      // longer absorbing typing — it only coalesces a tab through several fields.
    }, 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarKey]);

  // SPECULATIVE WORKBOOK BUILD, after the user has gone quiet.
  //
  // The export is the longest wait in the product (~29s on Dubai) and is ~90% xlsxwriter
  // XML that no rewrite makes faster, so the only lever left is to have it already built
  // when the button is pressed.
  //
  // THE SETTLE DELAY LIVES HERE, not on the server: the browser is what knows when the
  // last edit happened. Every dependency below is something that reaches the workbook, so
  // any change re-arms the timer from scratch — which is what "restart the rebuild if the
  // user makes any changes" means in practice. `result.summary` is the re-finalize
  // signal: it is a fresh object after a manual match, note, release, restore or carried
  // clear, and is deliberately UNCHANGED by the frames arriving (that merge spreads the
  // same reference through), so loading rows does not trigger a build.
  //
  // Correctness does not depend on this timer at all. A build the user outruns is not
  // stale — the server keys workbooks on a digest of the finalized result, so a
  // superseded one simply never matches again and the download rebuilds. The timer is
  // purely about not burning ~29s of a worker per keystroke.
  const PREBUILD_IDLE_MS = 25_000;
  useEffect(() => {
    if (!prematchId || applying || rebridging || exporting) return;
    const t = setTimeout(() => {
      api.prebuildExport({
        prematchId, insurerName, settings: reconSettings, sessionPairs,
        manualNotesCri: notesCri, manualNotesInsurer: notesInsurer,
      }).catch(() => { /* speculative: never surfaced */ });
    }, PREBUILD_IDLE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prematchId, sessionPairs, notesCri, notesInsurer, reconSettings, result.summary,
      applying, rebridging, exporting]);

  function triggerDownload(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  // A segment's downloads say which slice they hold in the file name, as in their titles.
  // AR also names the customer: one insurer has many, and the files must not collide.
  // Edge punctuation trimmed so "ABC L.L.C." does not leave "L_L_C__Recon".
  const customer = isAr
    ? (result.customer_name || "").replace(/^[^0-9A-Za-z]+|[^0-9A-Za-z]+$/g, "") : "";
  const token = ((insurerName + (customer ? " " + customer : "")
    + (result.segment_label ? " " + result.segment_label : ""))
    .replace(/[^0-9A-Za-z]+/g, "_")) || "Insurer";
  const exportArgs = {
    prematchId, insurerName, settings: reconSettings, sessionPairs,
    manualNotesCri: notesCri, manualNotesInsurer: notesInsurer,
  };

  function exportErrorMessage(e: unknown): string {
    return "Export failed: " + (e instanceof Error ? e.message : String(e));
  }

  async function runExport(kind: "xlsx" | "csv", pid: string) {
    const args = { ...exportArgs, prematchId: pid };
    if (kind === "csv") {
      triggerDownload(await api.exportCsv(args), `CRI_${token}_Recon_Summary.csv`);
      toast.success("Summary CSV downloaded.");
    } else {
      triggerDownload(await api.exportWorkbook(args), `CRI_${token}_Recon.xlsx`);
      toast.success("Annotated workbook downloaded.");
    }
  }

  // A 409 means the server-side prematch cache entry expired. We still hold the
  // uploaded files, so instead of dead-ending the user we transparently re-run the
  // reconciliation once and retry the export with the fresh prematch.
  async function doExport(kind: "xlsx" | "csv") {
    setExporting(true);
    try {
      await runExport(kind, prematchId);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        toast.info("Session data had expired — re-running the reconciliation, the export will follow…");
        try {
          await runExport(kind, await rerun(reconSettings, bridgeRejections));
        } catch (e2) {
          toast.error(exportErrorMessage(e2));
        }
      } else {
        toast.error(exportErrorMessage(e));
      }
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="app-main">
      <div className="sidebar-layout">
        <Sidebar settings={sidebar} onChange={onSidebarChange} collapsed={sidebarCollapsed}
          onToggle={onToggleSidebar} savedRates={savedRates} book={inputs.book}
          rateNoticeAck={rateNoticeAck} onAckRateNotice={onAckRateNotice}
          locked={filesLost
            ? "Settings are read-only after a page refresh: the uploaded files are no longer in this tab. Use Re-upload to change a tolerance, rate or period."
            : undefined}
          reconMonthEnd={reconSettings.recon_month ? monthEndISO(reconSettings.recon_month) : ""} />
        <div className="page-body">
          {/* Header */}
          <div className="page-head">
            <div className="page-head-left">
              <h2>CRI x {insurerTitle} — Reconciliation</h2>
              {result.segment_label && <span className="badge badge-muted">{result.segment_label}</span>}
              {result.mode && <span className={result.mode === "GL" ? "badge badge-gl" : "badge badge-muted"}>{result.mode}</span>}
              {(pillFrom || result.period_end) && (
                <span className="period-pill">{pillFrom ?? "…"} → {result.period_end ?? "…"}</span>
              )}
              <span className={reconciled ? "badge-reconciled" : "badge-unreconciled"}>
                {reconciled ? "RECONCILED" : "UNRECONCILED"}
              </span>
              <InfoTip text="RECONCILED means the total Difference is within your tolerance. The Difference is the bucketing residual: the in-period rows the app could place in no bucket at all, listed on the Other tab. Groups 1–3 below are open reconciling items — they are accounted for on both books and net out of the Difference, so clearing them does not move it." />
            </div>
            <div className="btn-row">
              <button className="btn" onClick={onReupload}>Re-upload</button>
            </div>
          </div>

          {result.segments && (
            <div className="segment-bar">
              <span className="muted header-label">Show</span>
              <div className="segmented" role="radiogroup" aria-label="Business shown">
                {result.segments.map(({ key, label }) => (
                  <button key={key || "all"} type="button" role="radio"
                    aria-checked={(result.segment ?? "") === key}
                    className={`seg-opt ${(result.segment ?? "") === key ? "active" : ""}`}
                    disabled={applying || rebridging}
                    onClick={() => void switchSegment(key)}>{label}</button>
                ))}
              </div>
              {result.segment_label && (
                <span className="muted segment-bar-note">
                  Every figure, tab and download below covers {result.segment_label} only.
                  {result.segment_note ? " " + result.segment_note : ""}
                  {(result.segment_cross_pairs ?? 0) > 0
                    ? ` ${result.segment_cross_pairs} CRI row${result.segment_cross_pairs === 1 ? " is" : "s are"} matched to an insurer row in the other view, so each half shows on its own view's Matched line.`
                    : ""}
                </span>
              )}
            </div>
          )}

          <hr className="rule" />

          {/* THE VERDICT, stated where the eye lands first. The Difference banner below
              the track columns is unchanged and still the figure finance is trained to
              read; this repeats the SAME number (s.difference) above the fold so the
              answer to "did this reconcile?" needs no scrolling. Icon + text label, not
              colour alone. */}
          {s.difference !== undefined && s.difference !== null && (
            <div className={`verdict-strip ${reconciled ? "verdict-strip--ok" : "verdict-strip--bad"}`}
              role="status">
              <span className="verdict-icon" aria-hidden="true">{reconciled ? "✓" : "!"}</span>
              <div className="verdict-body">
                <p className="verdict-title">{reconciled ? "Reconciled" : "Unreconciled"}</p>
                <div className="verdict-figure">Difference {fmtAed(s.difference)}</div>
                <p className="verdict-next">
                  {reconciled
                    ? "Within tolerance — nothing to action. Download the annotated workbook at the bottom of this page."
                    : "Outside tolerance. The Difference is the bucketing residual — "
                      // Until the rows are in hand the COUNT is unknown, and "no unplaced
                      // rows were found" is a claim, not a placeholder. Say the true thing
                      // that needs no count rather than flash a wrong one for a second.
                      + (!framesReady
                          ? `the in-period rows the app could place in no bucket, listed on the Other tab under "Unidentified" and "Identified but not reconciled".`
                          : residualCount > 0
                          ? `${residualCount.toLocaleString()} in-period row${residualCount === 1 ? "" : "s"} the app could place in no bucket, listed on the Other tab under "Unidentified" and "Identified but not reconciled".`
                          : "no unplaced rows were found, so it comes from the closing balances themselves.")
                      + (openItemCount > 0
                          ? ` Groups 1–3 below hold ${openItemCount.toLocaleString()} open item${openItemCount === 1 ? "" : "s"} still worth chasing, but they net out of the Difference and clearing them will not move it.`
                          : "")}
                </p>
              </div>
            </div>
          )}

          {/* Severity-ranked alerts. Same wording as before, ranked so a figure-affecting
              warning can never sit below an informational one, with the info tier folded
              away by default — see lib/alerts.ts. */}
          <AlertStack alerts={buildAlerts(result, { applyError, filesLost })} />
          {/* Everything below is derived from the reconciliation, so it goes provisional
              (dimmed + click-through disabled) while a re-run is in flight. The sidebar
              and page header stay live so settings can still be changed mid-run. */}
          <div className={applying || rebridging ? "busy-dim" : undefined}
            aria-busy={applying || rebridging}>

          {/* A supplied prior-period file that could not be read. Kept ABOVE the balance
              columns and full-width: it is an upload failure, not a figure. Without it the
              upload simply vanishes — the adjusted cards are gated on `unreconciled`, so a
              parse failure looks exactly like never having attached a file. */}
          {!result.unreconciled && result.unreconciled_error && (
            <div className="row" style={{ marginBottom: 16 }}>
              <div className="col kpi-card kpi-card--warn">
                <div className="kpi-label">Prior-period unreconciled file not read</div>
                <div className="muted stack-xs">
                  {result.unreconciled_error}
                </div>
              </div>
            </div>
          )}

          {/* ONE COLUMN PER BOOK: CRI down column 1, insurer down column 2. Each book's
              closing balance sits directly above its own Adjusted Closing, which sits above
              the item count explaining that adjustment, so a user follows one ledger
              straight down. The previous layout paired the cards by KIND across three rows
              (CRI|Insurer, then Insurer|Insurer, then CRI|CRI), flipping the meaning of the
              left column twice going down. Row/column placement is explicit so the two
              columns stay locked row-for-row — see .kpi-book-grid for why that needs a
              grid rather than two independent stacks. */}
          <div className="kpi-book-grid">
              <div className="kpi-card" style={{ gridColumn: 1, gridRow: 1 }}>
                <div className="kpi-label">
                  Balance as per CRI (full ledger, all tracks)
                  <InfoTip text="CRI = your internal ledger (the broker's books). This is its closing balance across every track." />
                </div>
                <div className="kpi-value">{fmtAed(criTotal)}</div>
                <OutsidePremiumNote show={isAr} amount={result.track_closings?.cri?.other} />
              </div>

              {/* The CRI side's own carried population (finance 2026-08-25). Shown only when
                  a CRI-side list was actually supplied, so an insurer-only upload renders
                  exactly what it did before. Without this the CRI items are counted in the
                  figures and listed on the group tabs but have no headline of their own. */}
              {result.unreconciled && (result.unreconciled.cri_open_count ?? 0) > 0 && (
                <>
                  <div className="kpi-card" style={{ gridColumn: 1, gridRow: 2 }}>
                    <div className="kpi-label">
                      Adjusted Closing Balance (CRI)
                      <InfoTip text="The CRI closing balance plus the prior-period items from the CRI-side unreconciled list that are STILL OPEN after this period. Items this period settled are excluded — they cleared. The Closing Balance and the Difference are unchanged." />
                    </div>
                    <div className="kpi-value">
                      {fmtAed(result.unreconciled.cri_adjusted_closing ?? 0)}
                    </div>
                    <div className="muted stack-xs">
                      {fmtAed(result.unreconciled.cri_closing ?? 0)} closing{" "}
                      {(result.unreconciled.cri_open_total ?? 0) >= 0 ? "+" : "−"}{" "}
                      {fmtAed(Math.abs(result.unreconciled.cri_open_total ?? 0))} still open
                    </div>
                  </div>
                  <div className="kpi-card" style={{ gridColumn: 1, gridRow: 3 }}>
                    <div className="kpi-label">
                      Prior-period items checked (CRI)
                      <InfoTip text="Every item in the CRI-side unreconciled list was checked against this period's CRI and insurer transactions, on reference or policy number plus amount." />
                    </div>
                    <div className="kpi-value">
                      {(result.unreconciled.cri_open_count ?? 0).toLocaleString()} still open
                    </div>
                    <div className="muted stack-xs">
                      {(result.unreconciled.cri_matched_count ?? 0).toLocaleString()} of{" "}
                      {((result.unreconciled.cri_matched_count ?? 0)
                        + (result.unreconciled.cri_open_count ?? 0)).toLocaleString()}
                      {" "}found in this period ({fmtAed(result.unreconciled.cri_matched_total ?? 0)})
                    </div>
                  </div>
                </>
              )}

              <div className="kpi-card" style={{ gridColumn: 2, gridRow: 1 }}>
                <div className="kpi-label">
                  Balance as per Insurer (full ledger, all tracks)
                  <InfoTip text="The closing balance of the insurer's Statement of Account (SOA) — the ledger the insurance company sent you." />
                </div>
                <div className="kpi-value">{fmtAed(insTotal)}</div>
                <OutsidePremiumNote show={isAr} amount={result.track_closings?.insurer?.other} />
                {result.merged_accounts && result.merged_accounts.filter((n) => n !== "ledger").length > 0 && (
                  <div className="muted stack-xs">
                    Merged statements: {result.merged_accounts.filter((n) => n !== "ledger").map((n) => n.replace(/_/g, " ")).join(" + ")}
                    <InfoTip text="This insurer's statement spans multiple accounts/files; they were merged into one ledger for this reconciliation." />
                  </div>
                )}
              </div>

              {/* Adjusted Closing Balance — only when finance supplied the prior-period
                  unreconciled file (GL). The Closing Balance above is deliberately left
                  untouched; this states the position once the carried-forward items that
                  are STILL OPEN after this period are allowed for. */}
              {result.unreconciled && (
                <>
                  <div className="kpi-card" style={{ gridColumn: 2, gridRow: 2 }}>
                    <div className="kpi-label">
                      Adjusted Closing Balance (Insurer)
                      <InfoTip text="The insurer closing balance plus the prior-period items from your unreconciled file that are STILL OPEN after this period. Items this period settled are excluded — they cleared. The Closing Balance and the Difference are unchanged." />
                    </div>
                    <div className="kpi-value">
                      {fmtAed(result.unreconciled.adjusted_closing)}
                    </div>
                    <div className="muted stack-xs">
                      {fmtAed(insTotal)} closing {result.unreconciled.open_total >= 0 ? "+" : "−"}{" "}
                      {fmtAed(Math.abs(result.unreconciled.open_total))} still open
                    </div>
                  </div>
                  <div className="kpi-card" style={{ gridColumn: 2, gridRow: 3 }}>
                    <div className="kpi-label">
                      Prior-period items checked
                      <InfoTip text="Every item in the unreconciled file was checked against this period's CRI and insurer transactions, on reference or policy number plus amount." />
                    </div>
                    <div className="kpi-value">
                      {result.unreconciled.open_count.toLocaleString()} still open
                    </div>
                    <div className="muted stack-xs">
                      {result.unreconciled.matched_count.toLocaleString()} of{" "}
                      {(result.unreconciled.matched_count + result.unreconciled.open_count).toLocaleString()}
                      {" "}found in this period ({fmtAed(result.unreconciled.matched_total)})
                    </div>
                  </div>
                </>
              )}
          </div>

          {/* Combined opening (centered) */}
          <div className="row row--center" style={{ marginTop: 16 }}>
            <div className="kpi-card kpi-card--narrow">
              <div className="kpi-label">
                Insurer SOA Opening Ref (combined ledger)
                <InfoTip text="The opening balance carried forward on the insurer's statement, across all merged accounts. 'Not detected' means the statement had no opening line." />
              </div>
              <div className="kpi-value">
                {opening === null || opening === undefined || result.insurer_opening_detected === false
                  ? "Not detected" : fmtAed(opening)}
              </div>
            </div>
          </div>

          {/* Live-tolerance caption */}
          <p className="muted text-center" style={{ marginTop: 12 }}>
            {/* The tolerances the figures on screen were computed WITH, not the sidebar
                boxes: those can move ahead of the result (mid re-run, or a failed one). */}
            Matched tolerance in effect — Premium: AED {(reconSettings.amount_tol_premium ?? sidebar.tolPremium).toFixed(2)}
            {!isAr && ` · Commission: AED ${(reconSettings.amount_tol_commission ?? sidebar.tolCommission).toFixed(2)}`}. Adjust in
            the sidebar; changes apply when you leave the field or press Enter.
          </p>

          {/* Two-column track view */}
          <div className="row" style={{ marginTop: 8 }}>
            <TrackColumn track="Premium" summary={premiumSummary} breakdown={breakdown} tolerance={tolerance}
              dfCri={result.df_cri} dfInsurer={result.df_insurer} displayConfig={result.display_config} insurerName={insurerName}
              framesReady={framesReady} isAr={isAr} />
            {!isAr && (
              <TrackColumn track="Commission" summary={commissionSummary} breakdown={breakdown} tolerance={tolerance}
                dfCri={result.df_cri} dfInsurer={result.df_insurer} displayConfig={result.display_config} insurerName={insurerName}
                framesReady={framesReady} />
            )}
          </div>

          {/* Centered Difference banner */}
          {s.difference !== undefined && s.difference !== null && (
            <div className="diff-banner-wrap">
              <div className="diff-banner">
                <div className={`diff-banner-inner ${reconciled ? "is-ok" : "is-bad"}`}>
                  Difference &nbsp; {fmtAed(s.difference)}
                  <InfoTip text="The unexplained residual: both closing balances combined, minus everything categorised into a bucket (incl. opening) — matched pairs cancel out. What's left is what the app couldn't explain; zero within tolerance = RECONCILED." />
                </div>
              </div>
            </div>
          )}

          <hr className="rule-section" />

          {/* Additional Details: filters + full statement tabs + manual match + notes */}
          <AdditionalDetails
            result={result}
            baseline={baseline}
            // The statement view is what makes the rows necessary, so it owns neither the
            // fetch nor the flag: ResultPage knows when they arrive, and opening the view
            // is what tells a re-run to ask for them inline.
            framesReady={framesReady}
            shown={detailsOpen}
            onShow={() => { setDetailsOpen(true); void loadFrames(prematchId); }}
            prematchId={prematchId}
            insurerName={insurerName}
            periodFromSetting={reconSettings.period_from}
            periodToSetting={reconSettings.period_to}
            // The tolerance the CURRENT result was computed with — the carried-pair tie
            // preview gates on exactly the number the engine used (reconcile.py passes
            // manual_tol=amount_match_tol), so it can never promise a settlement the
            // engine will refuse.
            amountTol={reconSettings.amount_match_tol}
            sessionPairs={sessionPairs}
            notesCri={notesCri}
            notesInsurer={notesInsurer}
            bridgeRejections={bridgeRejections}
            rebridging={rebridging}
            onResultUpdate={(r, pairs, full) => {
              setResult(r);
              setSessionPairs(pairs);
              // Complete frames replace the patch baseline, exactly as a re-run does.
              if (full) setBaseline(r);
            }}
            onNotesUpdate={(r, nc, ni) => { if (r) setResult(r); setNotesCri(nc); setNotesInsurer(ni); }}
            onBridgeReject={applyBridgeRejections}
            onPrematchExpired={renewPrematch}
            onCarriedPairsChanged={applyCarriedPairs}
          />

          {/* Exports */}
          <hr className="rule-section" />
          <div className="btn-row">
            <button className="btn" onClick={() => doExport("csv")} disabled={exporting}>
              {exporting ? "Preparing…" : "Download Summary CSV"}
            </button>
            <button className="btn btn-primary" onClick={() => doExport("xlsx")} disabled={exporting}>
              {exporting ? "Preparing…" : "Download Annotated Workbook"}
            </button>
          </div>
          {exporting && (
            <>
              <div className="progress-indeterminate" style={{ marginTop: 10, maxWidth: 420 }} />
              <p className="muted" style={{ margin: "6px 0 0" }}>
                {/* "~30 seconds" was measured on a small ledger and is not what the
                    largest ones cost: writing every transaction row to its group tab
                    is minutes of spreadsheet formatting on a 96k-row statement. An
                    under-promise here is not harmless — a user who believes it has
                    hung clicks again and starts a SECOND full build alongside the
                    first. The elapsed counter beside this is the honest signal. */}
                Building the export — a large statement can take a minute or more. The
                download starts automatically when it's ready; you don't need to click
                again.{elapsed > 0 && ` · ${elapsed}s elapsed`}
              </p>
            </>
          )}
          </div>
        </div>
      </div>
      {(applying || rebridging) && (
        <>
          <div className="busy-bar" />
          <div className="busy-label" role="status" aria-live="polite">
            <span className="spinner" />
            Applying change — re-running the reconciliation…{elapsed > 0 && ` ${elapsed}s`}
          </div>
        </>
      )}
    </div>
  );
}

/** AR shows ONE track panel, so a row on no track (Dubai's 0.45 VAT journal) left the
 *  headline and that panel's closing apart with nothing saying why. States the gap on
 *  the headline card. Display only: the amount is the engine's own track closing. */
function OutsidePremiumNote({ show, amount }: { show: boolean; amount?: number }) {
  if (!show || amount === undefined || Math.abs(amount) < 0.005) return null;
  return (
    <div className="muted stack-xs">
      Includes {fmtAed(amount)} outside the Premium track
      <InfoTip text="Rows the layout does not classify as a receivable movement still count in the statement's balance, so they are in this headline but not in the Premium panel below. They are listed on the Other tab." />
    </div>
  );
}

const ALERT_ICON: Record<Alert["severity"], string> = {
  critical: "⛔",
  warning: "⚠",
  info: "ⓘ",
};

/** Ranked alert list. Critical/warning always visible; info folds away by default. */
function AlertStack({ alerts }: { alerts: Alert[] }) {
  const [showInfo, setShowInfo] = useState(false);
  if (alerts.length === 0) return null;

  const loud = alerts.filter((a) => a.severity !== "info");
  const quiet = alerts.filter((a) => a.severity === "info");

  return (
    <div className="alert-stack">
      {loud.map((a) => <AlertRow key={a.id} alert={a} />)}
      {quiet.length > 0 && (
        <>
          <button type="button" className="notices-toggle" aria-expanded={showInfo}
            onClick={() => setShowInfo((v) => !v)}>
            <span className="notices-caret" aria-hidden="true">▸</span>
            {quiet.length} informational notice{quiet.length === 1 ? "" : "s"}
            {showInfo ? " — hide" : " — no action needed"}
          </button>
          {showInfo && quiet.map((a) => <AlertRow key={a.id} alert={a} />)}
        </>
      )}
    </div>
  );
}

function AlertRow({ alert }: { alert: Alert }) {
  return (
    <div className={`alert alert--${alert.severity}`}>
      <span className="alert-icon" aria-hidden="true">{ALERT_ICON[alert.severity]}</span>
      <div className="alert-body">
        <strong className="alert-title">{alert.title}</strong>
        {alert.body}
      </div>
    </div>
  );
}
